import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import './styles/App.css';
import { Provider, useSelector, useDispatch } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { configureStore } from './redux/Store';
import { fetchUserData, resetUserSessionData } from './redux/slice/user.slice';
import { SocketProvider, useSocket } from './context/SocketContext';
import { UploadQueueProvider } from './context/UploadQueueContext';
import { Toaster } from 'react-hot-toast';
import Maintenance from './pages/Maintenance';
import { fetchPublicGatewaySettings, setPublicSettings } from './redux/slice/gateway.slice';

// Layout
import SmoothScroll from './components/layout/SmoothScroll';

import ScrollToTop from './components/common/ScrollToTop';

// Routes
import UserRoutes from './routes/UserRoutes';
import { AdminRoute } from './routes/guards';
import GameLoader from './components/common/GameLoader';

const AdminRoutes = lazy(() => import('./routes/AdminRoutes'));

const { store, persistor } = configureStore();

/* ── Socket notification toast ── */
function SocketToast() {
  const { notification, dismissNotification } = useSocket();
  const location = useLocation();
  const path = location.pathname;
  if (!notification) return null;
  return (
    <div
      className={`fixed bottom-6 right-6 z-[9999] max-w-sm w-full px-5 py-4 rounded-2xl border shadow-2xl flex items-start gap-4 transition-all duration-500
        ${notification.type === 'success'
          ? 'bg-[#0c0c18] border-cyber-green/40 shadow-[0_0_30px_rgba(57,255,20,0.15)]'
          : 'bg-[#0c0c18] border-cyber-pink/40 shadow-[0_0_30px_rgba(255,0,102,0.15)]'}`}
    >
      <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 text-lg
        ${notification.type === 'success' ? 'bg-cyber-green/20 text-cyber-green' : 'bg-cyber-pink/20 text-cyber-pink'}`}>
        {notification.type === 'success' ? '✓' : '✕'}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-[10px] ${path.startsWith('/admin') ? 'font-sans' : 'font-cyber'} font-medium uppercase tracking-widest mb-1
          ${notification.type === 'success' ? 'text-cyber-green' : 'text-cyber-pink'}`}>
          {notification.type === 'success' ? 'ORDER CONFIRMED' : 'NOTIFICATION'}
        </p>
        <p className={`text-xs text-gray-300 ${path.startsWith('/admin') ? 'font-sans' : 'font-sans'} leading-relaxed`}>{notification.message}</p>
      </div>
      <button onClick={dismissNotification} className="text-gray-600 hover:text-white transition-colors flex-shrink-0 text-lg leading-none">×</button>
    </div>
  );
}

/* ── Global Redux store sync component ── */
function StoreSynchronizer() {
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const prevUserIdRef = useRef(null);
  const { socket } = useSocket();

  useEffect(() => {
    if (isAuthenticated && user) {
      const userId = user._id || user.id;
      if (prevUserIdRef.current && prevUserIdRef.current !== userId) {
        dispatch(resetUserSessionData());
      }
      prevUserIdRef.current = userId;
      dispatch(fetchUserData(userId));
    } else {
      prevUserIdRef.current = null;
      dispatch(resetUserSessionData());
    }
  }, [dispatch, isAuthenticated, user]);

  useEffect(() => {
    dispatch(fetchPublicGatewaySettings());
  }, []);

  useEffect(() => {
    if (!socket) return;

    const handleMaintenanceChange = (data) => {
      console.log("[App] Maintenance status update received:", data);
      dispatch(setPublicSettings(data));
    };

    socket.on("maintenance_status_changed", handleMaintenanceChange);

    return () => {
      socket.off("maintenance_status_changed", handleMaintenanceChange);
    };
  }, [socket, dispatch]);

  return null;
}

function AppContent() {
  const [isInitialLoading, setIsInitialLoading] = useState(() => {
    const isHome = window.location.pathname === '/';
    if (!isHome) {
      window.__LOADER_FINISHED__ = true;
    }
    return isHome;
  });
  const { publicSettings } = useSelector((state) => state.gateway || {});
  const { user } = useSelector((state) => state.auth || {});
  const isAdmin = user?.role && user.role.toLowerCase() === 'admin';
  const location = useLocation();
  const path = location.pathname;
  const isExcludedPath = path.startsWith('/admin') || path.startsWith('/login') || path.startsWith('/verify-otp') || path.startsWith('/reset-password') || path.startsWith('/forgot-password') || path === '/maintenance';

  if (!publicSettings) {
    return (
      <div className="min-h-screen bg-[#030305] flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-2 border-cyber-pink/20 border-t-cyber-pink animate-spin shadow-[0_0_15px_rgba(255,0,122,0.3)]"></div>
      </div>
    );
  }

  if (publicSettings?.maintenanceMode && !isAdmin && !isExcludedPath) {
    return <Navigate to="/maintenance" replace />;
  }

  if (!publicSettings?.maintenanceMode && path === '/maintenance') {
    window.location.href = '/';
    return null;
  }

  return (
    <SmoothScroll>
      {isInitialLoading && path === '/' && (
        <GameLoader onComplete={() => setIsInitialLoading(false)} />
      )}
      <Toaster
        position="bottom-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#0c0c18',
            color: '#fff',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '16px',
            fontFamily: path.startsWith('/admin') ? "'Inter', sans-serif" : "'Orbitron', sans-serif",
            fontSize: '11px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
            letterSpacing: '0.05em',
            textTransform: 'uppercase'
          },
          success: {
            iconTheme: {
              primary: '#00ffaa',
              secondary: '#0c0c18',
            },
            style: {
              border: '1px solid rgba(0, 255, 170, 0.25)',
              boxShadow: '0 0 15px rgba(0, 255, 170, 0.1)',
            },
          },
          error: {
            iconTheme: {
              primary: '#ff0055',
              secondary: '#0c0c18',
            },
            style: {
              border: '1px solid rgba(255, 0, 85, 0.25)',
              boxShadow: '0 0 15px rgba(255, 0, 85, 0.1)',
            },
          },
        }}
      />
      <Routes>
        <Route path="/admin/*" element={
          <AdminRoute>
            <Suspense fallback={
              <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
                <div className="w-12 h-12 rounded-full border-4 border-[#00f0ff]/20 border-t-[#00f0ff] animate-spin shadow-[0_0_15px_rgba(0,240,255,0.4)]"></div>
                <span className="text-xs font-cyber tracking-widest text-[#00f0ff] uppercase animate-pulse">Initializing Security Channel...</span>
              </div>
            }>
              <AdminRoutes />
            </Suspense>
          </AdminRoute>
        } />
        <Route path="/*" element={<UserRoutes />} />
        <Route path="/loader" element={<GameLoader />} />
      </Routes>
      <SocketToast />
      <ScrollToTop />
    </SmoothScroll>
  );
}

function App() {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <SocketProvider>
          <UploadQueueProvider>
            <StoreSynchronizer />
            <AppContent />
          </UploadQueueProvider>
        </SocketProvider>
      </PersistGate>
    </Provider>
  );
}

export default App;
